/**
 * Client-side image compression utilities
 * Compresses images before uploading to reduce file size
 */

/**
 * Compress an image file using Canvas API
 * @param {File} file - The image file to compress
 * @param {object} options - Compression options
 * @returns {Promise<File>} - Compressed image file
 */
export async function compressImage(file, options = {}) {
  const {
    maxWidth = 1920,
    maxHeight = 1920,
    quality = 0.85,
    mimeType = 'image/jpeg'
  } = options;

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => {
        // Calculate new dimensions
        let width = img.width;
        let height = img.height;

        if (width > maxWidth || height > maxHeight) {
          const aspectRatio = width / height;

          if (width > height) {
            width = maxWidth;
            height = maxWidth / aspectRatio;
          } else {
            height = maxHeight;
            width = maxHeight * aspectRatio;
          }
        }

        // Create canvas and draw resized image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Convert canvas to blob
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to compress image'));
              return;
            }

            // Create new File from blob
            const compressedFile = new File([blob], file.name, {
              type: mimeType,
              lastModified: Date.now()
            });

            resolve(compressedFile);
          },
          mimeType,
          quality
        );
      };

      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };

      img.src = e.target.result;
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsDataURL(file);
  });
}

/**
 * Compress multiple images
 * @param {FileList|File[]} files - Array of image files
 * @param {object} options - Compression options
 * @returns {Promise<File[]>} - Array of compressed files
 */
export async function compressImages(files, options = {}) {
  const fileArray = Array.from(files);
  const promises = fileArray.map(file => compressImage(file, options));
  return Promise.all(promises);
}

/**
 * Get image dimensions from file
 * @param {File} file - Image file
 * @returns {Promise<{width: number, height: number}>}
 */
export function getImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };

      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };

      img.src = e.target.result;
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsDataURL(file);
  });
}

/**
 * Check if an image needs compression
 * @param {File} file - Image file
 * @param {number} maxSize - Max file size in bytes (default 500KB)
 * @param {number} maxDimension - Max width/height (default 1920)
 * @returns {Promise<boolean>}
 */
export async function needsCompression(file, maxSize = 500 * 1024, maxDimension = 1920) {
  // Check file size
  if (file.size > maxSize) {
    return true;
  }

  // Check dimensions
  try {
    const { width, height } = await getImageDimensions(file);
    return width > maxDimension || height > maxDimension;
  } catch {
    return false;
  }
}

const imageUtils = {
  compressImage,
  compressImages,
  getImageDimensions,
  needsCompression
};

export default imageUtils;
